import { Dashboard } from "./constants";

// Deep links (deep-linking design): the bidirectional mapping between dashboard
// commands and canonical URLs.
//
// URL grammar:
//   /dashboard/t/{tenantSlug}/o/{organisationSlug}/s/{appSpaceSlug}
//   /dashboard/t/{...}/o/{...}/s/{...}/{screen}
//   /dashboard/t/{...}/o/{...}/s/{...}/{screen}/{entity}?tab={tabSlug}
//
// The command registry stays the single source of truth for what loads: a URL
// only ever replays as a command through DashboardHome.commandSent, and every
// command that has a canonical URL writes it to the address bar. Commands with
// no entry here (palette openers, refresh) never touch the URL.

// The screen segment for each addressable command. Order matters for nothing;
// keys are the command values from Dashboard.Commands.
const screenSegmentByCommand = {
    [Dashboard.Commands.ShowAppSpaceOverview]: "",
    [Dashboard.Commands.ShowInbox]: "inbox",
    [Dashboard.Commands.ShowProfile]: "profile",
    [Dashboard.Commands.ShowBillingDetails]: "billing",
    [Dashboard.Commands.ShowSubscriptions]: "subscriptions",
    [Dashboard.Commands.ShowTenantManagement]: "tenant",
    [Dashboard.Commands.ShowOrganisationManagement]: "organisation",
    [Dashboard.Commands.ManageAppSpace]: "app-space",
    [Dashboard.Commands.ShowHealthAndCompliance]: "health",
    [Dashboard.Commands.ShowJobOrchestrator]: "jobs",
    [Dashboard.Commands.ShowServiceInstance]: "app",
    [Dashboard.Commands.CreateManagedApi]: "new-api",
    [Dashboard.Commands.CreateManagedApp]: "new-app",
    [Dashboard.Commands.CreateManagedMcp]: "new-mcp",
    [Dashboard.Commands.ResetPassword]: "reset-password",
    [Dashboard.Commands.ShowNamingConfirmation]: "confirm-naming",
};

const commandByScreenSegment = Object.fromEntries(
    Object.entries(screenSegmentByCommand).map(([command, segment]) => [segment, command])
);

// One kebab-case slug per tab title, shared by URL writing (?tab=...) and tab
// selection on entry. Never derived from display copy at the call sites; this
// fold is the single definition.
export const slugifyTabTitle = (title) =>
    (title ?? "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");

// The canonical context prefix for the active workspace.
const contextPath = ({ tenantSlug, organisationSlug, appSpaceSlug }) =>
    `/dashboard/t/${tenantSlug}/o/${organisationSlug}/s/${appSpaceSlug}`;

/**
 * The canonical URL for a command, or null when the command has no URL (palette
 * openers, refresh) or the slugs are not all known yet (naming not confirmed).
 * entitySlug carries the product slug (or id) for app screens and the jobId for
 * the job orchestrator; tabSlug lands in the query string.
 */
export const deepLinkPathForCommand = ({ command, slugs, entitySlug, tabSlug }) => {
    if (
        !slugs?.tenantSlug ||
        !slugs?.organisationSlug ||
        !slugs?.appSpaceSlug ||
        !(command in screenSegmentByCommand)
    ) {
        return null;
    }

    const segment = screenSegmentByCommand[command];

    let path = contextPath(slugs);
    if (segment) {
        path += `/${segment}`;
    }
    if (entitySlug) {
        path += `/${encodeURIComponent(entitySlug)}`;
    }
    if (tabSlug) {
        path += `?tab=${encodeURIComponent(tabSlug)}`;
    }

    return path;
};

/**
 * Parses a matched deep-link route back into its parts, or null when the
 * location is not a deep link (the plain /dashboard routes). The screen
 * segment maps back to its command; unknown segments parse but carry no
 * command, so the caller can fall back to the overview.
 */
export const parseDeepLinkLocation = (match, location) => {
    const { tenantSlug, organisationSlug, appSpaceSlug, screen, entity } = match?.params ?? {};

    if (!tenantSlug || !organisationSlug || !appSpaceSlug) {
        return null;
    }

    const query = new URLSearchParams(location?.search ?? "");

    return {
        tenantSlug,
        organisationSlug,
        appSpaceSlug,
        screenSegment: screen ?? "",
        entitySlug: entity ? decodeURIComponent(entity) : undefined,
        tabSlug: query.get("tab") ?? undefined,
        command: commandByScreenSegment[screen ?? ""] ?? null,
    };
};

/**
 * Rebuilds the canonical form of a parsed deep link with corrected slugs (a
 * retired slug redirects to its current one) while keeping screen, entity and
 * query intact.
 */
// What a deep link navigates by: its path and ?tab=, the only query parameter parseDeepLinkLocation
// reads. Anything else in the query (?env=, ?range=) is view state a screen writes about itself.
export const deepLinkNavigationKey = (location) =>
    `${location?.pathname ?? ""}?tab=${new URLSearchParams(location?.search ?? "").get("tab") ?? ""}`;

export const canonicalisedPath = (parsed, currentSlugs) => {
    let path = contextPath(currentSlugs);
    if (parsed.screenSegment) {
        path += `/${parsed.screenSegment}`;
    }
    if (parsed.entitySlug) {
        path += `/${encodeURIComponent(parsed.entitySlug)}`;
    }
    if (parsed.tabSlug) {
        path += `?tab=${encodeURIComponent(parsed.tabSlug)}`;
    }
    return path;
};

// Section anchors: hand-assigned stable ids for deep-link fragments
// (#recent-activity). Never derived from display copy; renaming a heading must
// not break inbound links. Assign new ids here, reference them from screens.
export const DashboardSectionAnchors = {
    Overview: {
        Journey: "journey",
        QuickActions: "quick-actions",
        HealthAtAGlance: "health-at-a-glance",
        RecentActivity: "recent-activity",
    },
    Management: {
        Essentials: "essentials",
        // The Basics tab of the tenant, organisation and AppSpace pages, and the Settings tab after
        // it, with one anchor for each of its cards.
        Basics: "basics",
        Settings: "settings",
        AiCallTranscripts: "ai-call-transcripts",
        Teams: "teams",
    },
    App: {
        Admin: "admin-actions",
    },
    Infra: {
        QualityAssurance: "infra-quality-assurance",
        Staging: "infra-staging",
        Production: "infra-production",
        CpuUse: "infra-cpu-use",
        MemoryUse: "infra-memory-use",
        PodsReady: "infra-pods-ready",
    },
    Uptime: {
        CurrentStatus: "uptime-current-status",
        LastCheck: "uptime-last-check",
        Range: "uptime-range",
        OverTime: "uptime-over-time",
        ResponseTimes: "uptime-response-times",
    },
    FeatureFlags: {
        Toggles: "feature-flags-toggles",
        Setting: "feature-flags-setting",
        Criteria: "feature-flags-criteria",
        Usage: "feature-flags-usage",
        Keys: "feature-flags-keys",
    },
    Logs: {
        Search: "logs-search",
        Summary: "logs-summary",
        Findings: "logs-findings",
        RequestTrail: "logs-request-trail",
        Filters: "logs-filters",
        Records: "logs-records",
    },
    Traffic: {
        Availability: "availability",
        ApplicationTraffic: "application-traffic",
        ResponseTimeDistribution: "response-time-distribution",
        LatencyPercentiles: "latency-percentiles",
        StatusCodeDistribution: "status-code-distribution",
        ErrorRateDistribution: "error-rate-distribution",
        SlowestEndpoints: "slowest-endpoints",
        FastestEndpoints: "fastest-endpoints",
        TopEndpoints: "top-endpoints",
        TopConsumers: "top-consumers",
        RequestTrends: "request-trends",
        ResponseTimeTrends: "response-time-trends",
        RateLimiting: "rate-limiting",
        TrafficWindows: "traffic-windows",
        TrafficHeatmap: "traffic-heatmap",
    },
    TestCoverage: {
        Figures: "test-coverage-figures",
        LatestScan: "test-coverage-latest-scan",
        Problems: "test-coverage-problems",
        LineCoverage: "test-coverage-line-coverage",
        Tests: "test-coverage-tests",
        Builds: "test-coverage-builds",
        Apps: "test-coverage-apps",
        Trend: "test-coverage-trend",
        ScanHistory: "test-coverage-scan-history",
    },
    Compliance: {
        Kpis: "kpis",
        Distribution: "distribution",
        Trend: "trend",
        CheckHistory: "check-history",
        VulnerabilityManagement: "vulnerability-management",
        Dependencies: "dependencies-overview",
        DevSecOps: "devsecops-overview",
        Standards: "standards-overview",
    },
    Jobs: {
        ReleaseCheck: "release-check",
        AgentBudget: "agent-budget",
    },
};

// ============================================================================
// View state: what a screen writes into the query about itself (?env=..., ?range=...).
// The keys and what the address does with them are here, with the rest of its grammar; the
// mechanics (reading, writing, the hook) are in common/hooks/useViewStateParam.js, which is
// where screens take the keys from.
// ============================================================================

// The param keys, named once: public URL grammar, so a rename breaks inbound
// links the same way renaming a route segment would.
export const ViewStateParams = {
    Environment: "env",
    TimeRange: "range",
    // The level a screen that can be switched between levels is shown at (Health & Compliance):
    // the level's name in lower case, and nothing at the level the screen starts on.
    Level: "level",
    // The Logs tab's search. Together they carry the whole search, so its address is the search:
    // the mode, the window ("last" for a window that follows the clock, "from" and "to" for fixed
    // instants), free text, one param per filter field, and the record a link opens.
    LogMode: "mode",
    LogLast: "last",
    LogFrom: "from",
    LogTo: "to",
    LogText: "q",
    LogType: "type",
    LogOutcome: "outcome",
    LogOrigin: "origin",
    LogStatus: "status",
    LogOperation: "operation",
    LogTarget: "target",
    LogException: "exception",
    // The apps a search of several apps keeps, by the name each app's records carry.
    LogApp: "app",
    // A filter field's param names the values it keeps ("type=request"); its twin behind a
    // minus sign names the values it drops ("-type=trace").
    LogAppDropped: "-app",
    LogTypeDropped: "-type",
    LogOutcomeDropped: "-outcome",
    LogOriginDropped: "-origin",
    LogStatusDropped: "-status",
    LogOperationDropped: "-operation",
    LogTargetDropped: "-target",
    LogExceptionDropped: "-exception",
    LogMinDuration: "duration",
    LogRequest: "request",
    LogFeature: "feature",
    LogBaseline: "baseline",
    LogRelease: "release",
    LogAutoRefresh: "refresh",
    LogGroupBy: "group",
    LogRecord: "record",
    // The app that wrote the open record, in a search of several apps: a record is read from its
    // own app's logs, so a link to one names both.
    LogRecordApp: "recordapp",
    LogRecordView: "view",
    // The Infra tab's search in a view of several apps: the text its pods and containers table is
    // narrowed to. It is not held to the level, as the same apps go by the same names at every level.
    InfraSearch: "search",
};

// What every tab reads, so it stays in the address from tab to tab.
const SharedAcrossTabs = [ViewStateParams.Environment, ViewStateParams.Level];

// What only holds in the environment it was written in: an open record is in one environment's
// logs, so it does not come back to its tab once the environment has changed, and neither does
// the app that wrote it.
const HeldToEnvironment = [ViewStateParams.LogRecord, ViewStateParams.LogRecordApp];

// What only holds at the level it was written at: the apps a Logs search keeps and the record it
// has open are among the apps of that level, so they do not follow the screen to another level.
export const HeldToLevel = [
    ViewStateParams.LogApp,
    ViewStateParams.LogAppDropped,
    ViewStateParams.LogRecord,
    ViewStateParams.LogRecordApp,
];

const OwnedByTab = Object.values(ViewStateParams).filter((key) => !SharedAcrossTabs.includes(key));

/**
 * The address query after a move from the tab it names to another. A tab's view state is its
 * own: it leaves the address with the tab, so a link copied on the next tab does not carry it
 * and the next tab never reads it as its own, and it comes back when the tab is opened again.
 * What every tab reads (the environment, the level) stays where it is.
 * @param {string} search The address query as it stands.
 * @param {string} [tab] The tab being opened.
 * @param {Record<string, { own: Record<string, string>, shared: Record<string, string> }>} [left] What each tab left behind so far, by tab.
 * @returns {{ search: string, left: Record<string, { own: Record<string, string>, shared: Record<string, string> }> }} The query, and `left` with the tab now being left added.
 */
export const viewStateAcrossTabs = (search, tab, left = {}) => {
    const query = new URLSearchParams(search);
    const leaving = query.get("tab") ?? "";

    if (leaving === (tab ?? "")) {
        return { search: query.toString(), left };
    }

    const shared = Object.fromEntries(SharedAcrossTabs.map((key) => [key, query.get(key) ?? ""]));
    const own = Object.fromEntries(OwnedByTab.filter((key) => query.has(key)).map((key) => [key, query.get(key)]));

    Object.keys(own).forEach((key) => query.delete(key));

    const returning = left[tab ?? ""];
    const sameEnvironment = returning?.shared[ViewStateParams.Environment] === shared[ViewStateParams.Environment];
    const sameLevel = returning?.shared[ViewStateParams.Level] === shared[ViewStateParams.Level];

    Object.entries(returning?.own ?? {})
        .filter(([key]) => sameEnvironment || !HeldToEnvironment.includes(key))
        .filter(([key]) => sameLevel || !HeldToLevel.includes(key))
        .forEach(([key, value]) => query.set(key, value));

    if (tab) {
        query.set("tab", tab);
    } else {
        query.delete("tab");
    }

    return { search: query.toString(), left: { ...left, [leaving]: { own, shared } } };
};

/**
 * A level as the address holds it: its name in lower case.
 * @param {string} level
 */
export const formatLevelParam = (level) => level.toLowerCase();

/**
 * The level an address names (?level=organisation), out of the levels a screen offers: the one
 * the param holds, in any case. When the address names none of them, the level the screen starts
 * on.
 * @param {string | undefined} value The param as the address holds it.
 * @param {string[]} levels The levels the screen offers.
 * @param {string} startingLevel The level the screen starts on.
 */
export const parseLevelParam = (value, levels, startingLevel) =>
    levels.find((level) => formatLevelParam(level) === value?.toLowerCase()) ?? startingLevel;

/**
 * An address cut down to where it points: its path, its tab and the section after "#". The rest
 * of the query is view state, which can hold what a user typed (the Logs search) and values out
 * of a customer's logs. For whatever keeps a record of addresses: telemetry's page views.
 * @param {string} address A full address or a path, as written.
 */
export const navigationAddress = (address) => {
    const [beforeSection, ...section] = (address ?? "").split("#");
    const [path, ...query] = beforeSection.split("?");
    const tab = new URLSearchParams(query.join("?")).get("tab");

    return `${path}${tab ? `?tab=${encodeURIComponent(tab)}` : ""}${section.length > 0 ? `#${section.join("#")}` : ""}`;
};
